import { Link } from 'react-router-dom';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Avatar,
    Typography,
    Divider,
    Box,
    Switch,
    Collapse,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LoginIcon from '@mui/icons-material/Login';

export const SideNavbar = ({
    drawerOpen,
    toggleDrawer,
    isAuthenticated,
    user,
    userFullName,
    getInitials,
    loading,
    handleSignIn,
    authenticatedItems,
    unauthenticatedItems
}) => {
    // Don't render Drawer at all when closed
    if (!drawerOpen) return null;

    return (
        <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
            sx={{
                '& .MuiDrawer-paper': {
                    width: {
                        xs: '100vw',
                        sm: '100vw',
                        md: 300,
                        lg: 300,
                        xl: 300
                    },
                    maxWidth: '100vw'
                }
            }}
        >
            {/* User info header for authenticated users */}
            {isAuthenticated && user && (
                <>
                    <Box sx={{
                        p: { xs: 3, md: 2 },
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1
                        }}>
                            {/* User info */}
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    src={user?.photoUrl || undefined}
                                    sx={{
                                        width: { xs: 56, md: 48 },
                                        height: { xs: 56, md: 48 },
                                        mr: 2
                                    }}
                                    alt={userFullName || user.email}
                                >
                                    {!user?.photoUrl && getInitials(userFullName)}
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: { xs: '1.1rem', md: '1rem' }
                                        }}
                                    >
                                        {userFullName || 'User'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            opacity: 0.8,
                                            fontSize: { xs: '0.9rem', md: '0.875rem' }
                                        }}
                                    >
                                        {user.email}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Close button - only on mobile for authenticated users */}
                            <IconButton
                                onClick={toggleDrawer(false)}
                                aria-label="Close menu"
                                size="small"
                                sx={{ display: { xs: 'flex', md: 'none' } }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}

            <List>
                {/* For unauthenticated users - Sign in button with close button */}
                {!isAuthenticated && (
                    <>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={handleSignIn}
                                disabled={loading}
                                sx={{
                                    py: { xs: 2, md: 1.5 },
                                    px: { xs: 3, md: 2 }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: { xs: 48, md: 40 } }}>
                                    <LoginIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Sign in"
                                    slotProps={{
                                        primary: {
                                            fontSize: { xs: '1rem', md: '0.875rem' }
                                        }
                                    }}
                                />
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleDrawer(false)();
                                    }}
                                    size="small"
                                    aria-label="Close menu"
                                    sx={{
                                        display: { xs: 'flex', md: 'none' },
                                        ml: 1
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </ListItemButton>
                        </ListItem>

                        {/* Render unauthenticated items */}
                        {unauthenticatedItems.map((item) => (
                            <DrawerMenuItem key={item.label} item={item} loading={loading} />
                        ))}
                    </>
                )}

                {/* For authenticated users - all menu items */}
                {isAuthenticated && authenticatedItems.map((item) => (
                    <DrawerMenuItem key={item.label} item={item} loading={loading} />
                ))}
            </List>
        </Drawer>
    );
};

// Separate component for individual drawer menu items
const DrawerMenuItem = ({ item, loading }) => {
    const { label, to, onClick, icon, divider, isSwitch, checked, isCollapsible, expanded, onToggle, subItems } = item;

    return (
        <div>
            {divider && <Divider sx={{ my: 1 }} />}
            <ListItem disablePadding>
                <ListItemButton
                    component={to ? Link : 'button'}
                    to={to}
                    onClick={(e) => {
                        if (isSwitch || (onClick && !to) || isCollapsible) {
                            e.preventDefault();
                        }
                        if (isCollapsible) {
                            onToggle && onToggle();
                        } else {
                            onClick && onClick(e);
                        }
                    }}
                    disabled={loading}
                    sx={{
                        py: { xs: 2, md: 1.5 },
                        px: { xs: 3, md: 2 }
                    }}
                >
                    {icon && (
                        <ListItemIcon sx={{ minWidth: { xs: 48, md: 40 } }}>
                            {icon}
                        </ListItemIcon>
                    )}
                    <ListItemText
                        primary={label}
                        slotProps={{
                            primary: { sx: { fontSize: '0.875rem', color: 'text.primary' } },
                        }}
                    />
                    {isSwitch && (
                        <Switch
                            edge="end"
                            checked={checked}
                            slotProps={{
                                input: {
                                    'aria-labelledby': 'theme-switch'
                                }
                            }}
                            onClick={(e) => e.preventDefault()}
                        />
                    )}
                    {isCollapsible && (
                        expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    )}
                </ListItemButton>
            </ListItem>

            {/* Collapsible sub-items */}
            {isCollapsible && subItems && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {subItems.map((subItem) => (
                            <ListItem key={subItem.label} disablePadding>
                                <ListItemButton
                                    component={subItem.to ? Link : 'button'}
                                    to={subItem.to}
                                    onClick={subItem.onClick}
                                    sx={{
                                        pl: { xs: 6, md: 4 },
                                        py: { xs: 1.5, md: 1 },
                                        pr: { xs: 3, md: 2 }
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: { xs: 40, md: 32 } }}>
                                        {subItem.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={subItem.label}
                                        slotProps={{
                                            primary: {
                                                fontSize: { xs: '0.9rem', md: '0.8rem' }
                                            }
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Collapse>
            )}
        </div>
    );
};